// ante-react-kit/src/components/v2/AntVoucherV2.js
import React, { useRef, useImperativeHandle, forwardRef, useEffect, useCallback, useMemo } from 'react';
import AntGridScope, { buildGridScopeSegment } from '../AntGridScope';
import { LoadPanel } from 'devextreme-react/load-panel';
import stringify from '../../utils/stableStringify';

import AntCondition from '../../utils/AntCondition';
import AntLocalization from '../../utils/AntLocalization';
import { getDrilldown } from '../AntVoucherFlow/flow-config';

import AntPage from '../AntPage';
import AntToolbar from '../AntToolbar';
import AntBaseForm from '../AntBaseForm';
import AntVoucherTabs from '../AntVoucherTabs';
import AntErrorPage from '../AntErrorPage';
import AntVoucherSkeleton from '../AntVoucherSkeleton';
import AntErrorPanel from '../AntErrorPanel';
import AntInfoPanel from '../AntInfoPanel';
import AntMessageBox from '../AntMessageBox';
import AntPrinting from '../AntPrinting';
import AntVoucherFlow from '../AntVoucherFlow';
import AntLeftSidebar from '../AntLeftSidebar';
import AntHeaderCard from './AntHeaderCard';

import useVoucherData from './hooks/useVoucherData';
import useVoucherActions from './hooks/useVoucherActions';
import useVoucherPrint from './hooks/useVoucherPrint';
import useAutoReload from './hooks/useAutoReload';

var AntVoucherV2 = forwardRef(function AntVoucherV2(props, ref) {
    // --- Refs ---
    var headerRef = useRef(null);
    var footerRef = useRef(null);
    var tabsRef = useRef(null);
    var printerRef = useRef(null);

    // --- Hooks ---
    var vData = useVoucherData(props);
    var loadContentRef = useRef(vData.loadContent);
    loadContentRef.current = vData.loadContent;

    var autoReload = useAutoReload(loadContentRef, vData.lockRef, vData.mountedRef);

    var vActions = useVoucherActions(vData, autoReload);
    vActions.propsRef.current = props;

    var paramsRef = useRef({ code: vData.mainCodeRef.current, id: vData.mainIDRef.current });
    paramsRef.current = { code: vData.mainCodeRef.current, id: vData.mainIDRef.current };

    // Khung xám thay vòng xoay: chỉ khi đang mở phiếu mới và chưa có dữ liệu.
    var renderBootSkeleton = Boolean(vData.isRenderPage && !vData.isPermDeniedData && vData.bootCode && !vData.mainState.error);

    var vPrint = useVoucherPrint({
        printerRef: printerRef,
        printComponentRef: props.printComponent,
        printUrl: props.printUrl,
        API: props.API,
        service: props.service,
        postActionFn: vActions.postAction,
        paramsRef: paramsRef
    });

    // --- imperativeHandle ---
    useImperativeHandle(ref, function () {
        return {
            showPopup: vActions.showPopup,
            postAction: vActions.postAction,
            load: function () { return vData.loadContent({ recalcable: true }); },
            autoReload: function () {
                if (props.flow) {
                    autoReload.start(false);
                    return vData.loadContent({ recalcable: true, isRefresh: true, forcePrepare: true, bypassSnapshot: true });
                }
                autoReload.start(true);
            },
            getFormData: function () { return vData.mainData; },
            runPrint: vPrint.runPrint
        };
    }, [vActions.showPopup, vActions.postAction, vData.loadContent, autoReload, vData.mainData, vPrint.runPrint]);

    // --- Toolbar action callbacks ---
    var onActionRelease = useCallback(async function () {
        if (props.onBeforeAction && (await props.onBeforeAction('release')) === false) return;
        var result = await vActions.postAction('release');
        if (props.onAfterAction) await props.onAfterAction('release', result);
    }, [props.onBeforeAction, props.onAfterAction, vActions.postAction]);

    var onActionClose = useCallback(async function () {
        if (props.onBeforeAction) await props.onBeforeAction('close');
        var result = await vActions.postAction('close');
        if (props.onAfterAction) await props.onAfterAction('close', result);
    }, [props.onBeforeAction, props.onAfterAction, vActions.postAction]);

    var onActionCancel = useCallback(async function () {
        if (props.onBeforeAction) await props.onBeforeAction('cancel');
        var result = await vActions.postAction('cancel');
        if (props.onAfterAction) await props.onAfterAction('cancel', result);
    }, [props.onBeforeAction, props.onAfterAction, vActions.postAction]);

    var onActionDelete = useCallback(async function () {
        if (props.onBeforeAction) await props.onBeforeAction('delete');
        var result = await vActions.postAction('delete');
        if (props.onAfterAction) await props.onAfterAction('delete', result);
    }, [props.onBeforeAction, props.onAfterAction, vActions.postAction]);

    var onActionPost = useCallback(async function () {
        if (props.onBeforeAction) await props.onBeforeAction('post');
        var result = await vActions.postAction('post');
        if (props.onAfterAction) await props.onAfterAction('post', result);
    }, [props.onBeforeAction, props.onAfterAction, vActions.postAction]);

    var onActionApprove = useCallback(async function () {
        if (props.onBeforeAction) await props.onBeforeAction('approve');
        var result = await vActions.postAction('approve');
        if (props.onAfterAction) await props.onAfterAction('approve', result);
    }, [props.onBeforeAction, props.onAfterAction, vActions.postAction]);

    var onActionReject = useCallback(async function () {
        if (props.onBeforeAction) await props.onBeforeAction('reject');
        var result = await vActions.postAction('reject');
        if (props.onAfterAction) await props.onAfterAction('reject', result);
    }, [props.onBeforeAction, props.onAfterAction, vActions.postAction]);

    var onAction = useCallback(async function (btn) {
        if (props.onBeforeAction) await props.onBeforeAction(btn && btn.name);
        var result;
        if (props.onAction) {
            result = await props.onAction(btn);
        }
        if (props.onAfterAction) await props.onAfterAction(btn && btn.name, result);
        return result;
    }, [props.onBeforeAction, props.onAction, props.onAfterAction]);

    var onActionQuickCreate = useCallback(function () { vActions.quickCreate(); }, [vActions.quickCreate]);
    var onActionClone = useCallback(function () { vActions.clone(); }, [vActions.clone]);

    var onPrintClick = useCallback(function () { vPrint.runPrint(); }, [vPrint.runPrint]);
    var onRecalculateClick = useCallback(async function () { await vActions.postAction('recalculate'); }, [vActions.postAction]);

    var onRefreshClick = useCallback(async function () {
        await vData.loadContent({ recalcable: true, isRefresh: true, forcePrepare: true, bypassSnapshot: true });
        requestAnimationFrame(function () {
            if (tabsRef.current && tabsRef.current.reload) tabsRef.current.reload({ forceQuery: true, byPassGetCache: true });
        });
    }, [vData.loadContent]);

    var onToggleFlow = useCallback(function () {
        vData.setFlowVisible(function (v) { return !v; });
    }, [vData.setFlowVisible]);

    var onHomeClick = useCallback(function () {
        autoReload.stop();
        vData.navigateHome();
    }, [autoReload, vData.navigateHome]);

    var onActionProcessing = useCallback(function (btn) {
        if (btn && btn.name === 'showSwitchUI' && props.switchModeURL) {
            if (vData.mainData && vData.mainData.code) {
                vData.navigate(props.switchModeURL + '/' + vData.mainData.code);
            } else {
                vData.navigate('/home');
            }
            window.location.reload();
            return;
        }
        if (props.onActionProcessing) props.onActionProcessing(btn);
        vData.setMainState(function (v) {
            return Object.assign(v, { toolbarProcessingButton: btn ? btn.name : null });
        });
    }, [vData.mainData, props.switchModeURL, props.onActionProcessing]);

    var onActionProceeed = useCallback(function () {
        vData.setMainState(function (v) {
            return Object.assign(v, { toolbarProcessingButton: null });
        });
    }, []);

    // --- onPrinted ---
    useEffect(function setupAfterPrint() {
        if (props.onPrinted && props.toolbar && props.toolbar.showPrint && ((props.printComponent && props.printComponent.current) || props.printUrl)) {
            window.addEventListener('afterprint', props.onPrinted);
        }
        return function () {
            if (props.onPrinted) window.removeEventListener('afterprint', props.onPrinted);
        };
    }, []);

    // --- Derived ---
    var isRender = props.shouldRender ? props.shouldRender : 1;
    var titleText = props.title ? AntLocalization.translateText(props.title) : AntLocalization.translateText(vData.mainEntityDisplay);

    var flowToggle = useMemo(function () {
        if (!props.flow) return undefined;
        return {
            visible: vData.flowVisible,
            hasData: vData.flowData && vData.flowData.nodes && vData.flowData.nodes.length > 1,
            onClick: onToggleFlow
        };
    }, [props.flow, vData.flowVisible, vData.flowData, onToggleFlow]);

    // --- Render ---
    return (
        <AntGridScope segment={buildGridScopeSegment('voucher', props.table || (props.API && props.API.entityName))}>
            {isRender && (
                <AntPage
                    className={'ant-voucher-page'}
                    marginLeft={10}
                    marginRight={10}
                    marginBottom={props.marginBottom ? props.marginBottom : 5}
                    backgroundImage={props.backgroundImage}
                    hideBackgroundImage={vData.renderLoading || vData.renderAccessDenied || vData.renderNotExistData}
                >
                    <AntToolbar
                        API={props.API}
                        table={props.table}
                        shouldRender={isRender}
                        parentData={vData.mainData}
                        renderParent={Boolean(vData.isRenderPage && !vData.isInValidData && vData.isPreparedRef.current)}
                        title={titleText}
                        titleIcon={props.titleIcon}
                        processingButton={vData.mainState.toolbarProcessingButton}
                        showBack={true}
                        showHome={true}
                        showHelp={props.toolbar ? props.toolbar.showHelp : false}
                        showRefresh={true}
                        showSwitchUI={
                            props.switchModeURL
                                ? (String(props.switchModeURL).indexOf('/pos/') === 0 ? AntCondition.canUsePOS() : true)
                                : false
                        }
                        showPrint={props.toolbar ? props.toolbar.showPrint : false}
                        showRecalculate={props.toolbar && props.toolbar.showRecalculate !== undefined ? props.toolbar.showRecalculate : true}
                        showActions={props.toolbar ? props.toolbar.showActions : undefined}
                        actionPack={props.toolbar ? props.toolbar.actionPack : undefined}
                        items={props.toolbar ? props.toolbar.items : undefined}
                        onRefreshClick={onRefreshClick}
                        onHomeClick={onHomeClick}
                        onPrintClick={onPrintClick}
                        onRecalculateClick={onRecalculateClick}
                        onActionQuickCreate={onActionQuickCreate}
                        onActionClone={onActionClone}
                        onActionCancel={onActionCancel}
                        onActionDelete={onActionDelete}
                        onActionRelease={onActionRelease}
                        onActionClose={onActionClose}
                        onActionPost={onActionPost}
                        onActionApprove={onActionApprove}
                        onActionReject={onActionReject}
                        onAction={onAction}
                        onActionProcessing={onActionProcessing}
                        onActionProceeed={onActionProceeed}
                        actionRelease={props.actionRelease}
                        actionPost={props.actionPost}
                        flowToggle={flowToggle}
                    />

                    {vData.renderAccessDenied && (
                        <AntErrorPage
                            id={'ant-voucher-error-page-' + vData.mainEntityName}
                            text={AntLocalization.translateText({ vi: 'Không có quyền truy cập !', en: 'Access Denied', fr: 'Accès refusé', es: 'Acceso denegado', id: 'Akses ditolak', ja: 'アクセス拒否', zh: '访问被拒绝', ko: '접근 거부', hi: 'पहुँच अस्वीकृत', ar: 'تم رفض الوصول' })}
                        />
                    )}
                    {renderBootSkeleton && (
                        <AntVoucherSkeleton
                            showHeader={Boolean(props.header && props.header.items instanceof Array && props.header.items.length > 0)}
                            tabs={props.tabs instanceof Array && props.tabs.length > 0 ? props.tabs.length : 4}
                        />
                    )}
                    {vData.renderLoading && !renderBootSkeleton && (
                        <div>
                            <LoadPanel
                                position={{ of: '#ant-main-layout-drawer-scroll' }}
                                visible={true}
                                showPane={false}
                                shading={true}
                                wrapperAttr={{ class: 'ant-loading' }}
                            />
                        </div>
                    )}
                    {vData.renderNotExistData && (
                        <AntErrorPage
                            errors={vData.mainState.error ? vData.mainState.error.values : undefined}
                            id={'ant-voucher-error-page-' + vData.mainEntityName}
                            text={
                                vData.mainState.error
                                    ? AntLocalization.translateText(vData.mainState.error.text)
                                    : AntLocalization.translateText({ vi: 'Dữ liệu không tồn tại !', en: 'Data not found !', fr: 'Données introuvables !', es: '¡Datos no encontrados!', id: 'Data tidak ditemukan !', ja: 'データが存在しません！', zh: '数据不存在！', ko: '데이터가 없습니다!', hi: 'डेटा मौजूद नहीं है !', ar: 'البيانات غير موجودة !' })
                            }
                        />
                    )}
                    {vData.isRenderPage && !vData.isPermDeniedData && (
                        <div style={{ display: vData.isVisiblePage && vData.isPreparedRef.current ? 'flex' : 'none', flexDirection: 'row', flex: 1 }}>
                            {(!props.leftSidebar || props.leftSidebar.enabled !== false) && (
                                <AntLeftSidebar
                                    variant={(props.leftSidebar && props.leftSidebar.variant) || 'voucher'}
                                    data={{
                                        summary: vData.mainData,
                                        flowData: vData.flowData,
                                        entityName: vData.mainEntityName,
                                        entityDisplay: vData.mainEntityDisplay
                                    }}
                                    panels={props.leftSidebar && props.leftSidebar.panels}
                                    mockMode={props.leftSidebar && props.leftSidebar.mockMode}
                                />
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <p />
                                {vData.isVisibleHeader && props.headerSections && props.headerSections.sections ? (
                                    <AntHeaderCard
                                        sections={props.headerSections.sections}
                                        items={props.header && props.header.items ? props.header.items : []}
                                        formData={vData.mainData}
                                        oldData={vData.mainState.oldData}
                                        status={vData.mainData ? vData.mainData[props.headerSections.statusField || 'order_status'] : null}
                                        statusField={props.headerSections.statusField || 'order_status'}
                                        fieldConfigs={props.headerSections.fieldConfigs || null}
                                        table={props.header && props.header.table ? props.header.table : props.table}
                                        API={props.API}
                                        session={props.session}
                                        onFieldDataChanged={vData.onFieldDataChanged}
                                        disabled={vData.mainState.toolbarProcessingButton ? true : props.header ? props.header.disabled : undefined}
                                    />
                                ) : vData.isVisibleHeader ? (
                                    <AntBaseForm
                                        ref={headerRef}
                                        shouldRender={isRender}
                                        API={props.API}
                                        type={'Update'}
                                        formData={vData.mainData}
                                        formTable={vData.mainTable}
                                        oldData={vData.mainState.oldData}
                                        table={props.header && props.header.table ? props.header.table : props.table}
                                        alignItemLabelsInAllGroups={props.header ? props.header.alignItemLabelsInAllGroups : undefined}
                                        labelLocation={props.header ? props.header.labelLocation : undefined}
                                        colCount={props.header && props.header.colCount !== undefined ? props.header.colCount : 1}
                                        colCountDefault={props.header ? props.header.colCountDefault : undefined}
                                        fields={props.header && props.header.fields ? props.header.fields : []}
                                        items={props.header && props.header.items ? props.header.items : []}
                                        postFields={props.header ? props.header.postFields : undefined}
                                        onFieldDataChanged={vData.onFieldDataChanged}
                                        prepareConditions={props.header ? props.header.prepareConditions : undefined}
                                        onError={vData.setError}
                                        visible={vData.isVisiblePage}
                                        isSearching={props.header ? props.header.isSearching : undefined}
                                        disabled={vData.mainState.toolbarProcessingButton ? true : props.header ? props.header.disabled : undefined}
                                    />
                                ) : null}
                                {vData.isVisibleHeader && <p style={{ marginBlockStart: '0px', marginBlockEnd: '10px' }} />}
                                {vData.mainState.error && (
                                    <div className={'ant-voucher-error-container'}>
                                        <AntErrorPanel
                                            errors={vData.mainState.error.values}
                                            id={'ant-voucher-error-' + vData.mainEntityName}
                                            text={vData.mainState.error.text}
                                            hasBackground={true}
                                        />
                                        {vData.isVisibleHeader && <p style={{ marginBlockStart: '0px', marginBlockEnd: '10px' }} />}
                                    </div>
                                )}

                                {vData.mainInfos.length > 0 && <p style={{ marginBlockStart: '0px', marginBlockEnd: '10px' }} />}
                                {vData.mainInfos.map(function (info, i) {
                                    if (info.type === 'Warning' || info.type === 'Error') {
                                        return (
                                            <div key={'ant-voucher-error-container-' + i} className={'ant-voucher-error-container'}>
                                                <AntErrorPanel
                                                    id={'ant-voucher-error-' + vData.mainEntityName + '-' + i}
                                                    text={info.message}
                                                    hasBackground={true}
                                                />
                                                <p style={{ marginBlockStart: '0px', marginBlockEnd: '10px' }} />
                                            </div>
                                        );
                                    }
                                    return (
                                        <div key={'ant-voucher-info-' + i} className={'ant-voucher-info-container'}>
                                            <AntInfoPanel
                                                id={'ant-voucher-info-' + vData.mainEntityName + '-' + i}
                                                text={info.message}
                                                showYes={info.agreeable}
                                                onAgreeClick={function (e, done) {
                                                    if (props.onInfoAgree) props.onInfoAgree(info, done);
                                                }}
                                            />
                                            <p style={{ marginBlockStart: '0px', marginBlockEnd: '10px' }} />
                                        </div>
                                    );
                                })}
                                {props.flow && vData.flowData && vData.flowData.nodes && vData.flowData.nodes.length > 1 && (
                                    <div className={'ant-voucher-flow-container' + (!vData.flowVisible ? ' collapsed' : '')}>
                                        <AntVoucherFlow
                                            flowData={vData.flowData}
                                            currentNode={props.flow.currentNode || (props.API ? props.API.entityName : undefined)}
                                            currentCode={vData.mainCodeRef.current}
                                            height={props.flow.height || 140}
                                            showExpected={props.flow.showExpected !== false}
                                            showMeta={props.flow.showMeta !== false}
                                            compact={props.flow.compact || false}
                                            configOverride={props.flow.configOverride}
                                            accessScreens={props.session && props.session.settings && props.session.settings.access_screens}
                                            onNodeClick={function (node) {
                                                if (props.flow.onNodeClick) {
                                                    props.flow.onNodeClick(node);
                                                } else {
                                                    var drilldown = getDrilldown(node.type);
                                                    var _drillUrl = node.drilldownUrl || drilldown.url;
                                                    var _as = props.session && props.session.settings && props.session.settings.access_screens;
                                                    var _hasAccess = !drilldown.whenUserScreen || !_as || !Array.isArray(_as) || _as.length === 0 || _as.includes(drilldown.whenUserScreen);
                                                    if (!node.disabled && !node.noAccess && _hasAccess && _drillUrl && node.existence === 'exists' && node.code) {
                                                        if (drilldown.openInNewTab) window.open(_drillUrl + node.code, '_blank');
                                                        else vData.navigate(_drillUrl + node.code);
                                                    }
                                                }
                                            }}
                                        />
                                    </div>
                                )}
                                {props.tabs instanceof Array && props.tabs.length > 0 && (
                                    <div style={{ visibility: vData.isVisiblePage ? 'visible' : 'hidden', marginTop: '10px', marginBottom: '10px' }}>
                                        <AntVoucherTabs
                                            ref={tabsRef}
                                            shouldRender={isRender}
                                            API={props.API}
                                            formData={vData.mainData}
                                            formTable={vData.mainTable}
                                            oldData={vData.mainState.oldData}
                                            tabs={props.tabs}
                                            tabStyle={vData.mainState.tabStyle}
                                            tabHeight={props.tabHeight}
                                            autoExpandAll={props.tabAutoExpandAll}
                                            groupCollapsible={props.tabCollapsible}
                                            groupExpandMultiple={props.tabExpandMultiple}
                                            autoHideTitle={props.tabAutoHideTitle}
                                            onFieldDataChanged={vData.onFieldDataChanged}
                                            onDataSourceEdited={vData.reload}
                                            onAfterAction={vData.reload}
                                            onBarcodeScanned={props.onBarcodeScanned}
                                            onError={vData.setError}
                                            visible={vData.isVisiblePage}
                                            disabled={vData.mainState.toolbarProcessingButton ? true : undefined}
                                        />
                                    </div>
                                )}
                                {vData.isVisibleFooter && props.tabs && props.tabs.length > 0 && <p style={{ marginBlockStart: '0px', marginBlockEnd: '50px' }} />}
                                {vData.isVisibleFooter && (
                                    <AntBaseForm
                                        ref={footerRef}
                                        shouldRender={isRender}
                                        API={props.API}
                                        type={'Update'}
                                        formData={vData.mainData}
                                        formTable={vData.mainTable}
                                        oldData={vData.mainState.oldData}
                                        table={props.footer && props.footer.table ? props.footer.table : props.table}
                                        alignItemLabelsInAllGroups={props.footer ? props.footer.alignItemLabelsInAllGroups : undefined}
                                        labelLocation={props.footer ? props.footer.labelLocation : undefined}
                                        colCount={props.footer && props.footer.colCount !== undefined ? props.footer.colCount : 1}
                                        colCountDefault={props.footer ? props.footer.colCountDefault : undefined}
                                        fields={props.footer && props.footer.fields !== undefined ? props.footer.fields : []}
                                        items={props.footer && props.footer.items !== undefined ? props.footer.items : []}
                                        postFields={props.footer ? props.footer.postFields : undefined}
                                        onFieldDataChanged={vData.onFieldDataChanged}
                                        onError={vData.setError}
                                        visible={vData.isVisiblePage}
                                        isSearching={props.footer ? props.footer.isSearching : undefined}
                                        disabled={vData.mainState.toolbarProcessingButton ? true : props.footer ? props.footer.disabled : undefined}
                                    />
                                )}
                                <AntMessageBox ref={vActions.msgBoxRef} />
                            </div>
                        </div>
                    )}
                </AntPage>
            )}
            {props.children}
            <div style={{ display: 'none' }}>
                <AntPrinting ref={printerRef} onReady={vPrint.onPrintReady} />
            </div>
        </AntGridScope>
    );
});

AntVoucherV2.displayName = 'AntVoucherV2';
export default AntVoucherV2;
